import type { ReactNode } from "react";
import { cn } from "@/components/ui/cn";
import { Body, Caption } from "@/components/ui/typography";

type VTimelineItem = {
  id: string;
  title: ReactNode;
  description?: ReactNode;
  timestamp: string;
  icon?: ReactNode;
  tone?: "neutral" | "primary" | "success" | "warning" | "error";
};

const toneClass = {
  neutral: "bg-neutral-200 text-neutral-700",
  primary: "bg-primary-100 text-primary-700",
  success: "bg-success-100 text-success-700",
  warning: "bg-warning-100 text-warning-700",
  error: "bg-error-100 text-error-700",
} as const;

type VTimelineProps = {
  items: ReadonlyArray<VTimelineItem>;
  className?: string;
};

export function VTimeline({ items, className }: VTimelineProps): React.JSX.Element {
  return (
    <ol className={cn("relative space-y-4 border-l border-border pl-6", className)}>
      {items.map((item) => (
        <li key={item.id} className="relative">
          <span
            className={cn(
              "absolute -left-[33px] flex size-6 items-center justify-center rounded-full ring-4 ring-card",
              toneClass[item.tone ?? "neutral"],
            )}
          >
            {item.icon}
          </span>
          <div className="space-y-1">
            <Body weight="medium">{item.title}</Body>
            {item.description ? <Caption tone="muted">{item.description}</Caption> : null}
            <Caption tone="muted">{item.timestamp}</Caption>
          </div>
        </li>
      ))}
    </ol>
  );
}

export type { VTimelineItem, VTimelineProps };
